import { StationFilterChipsList } from '@app/components/stations/advanced-filter/display/StationFilterChipsList';
import { getStationTypeLabel } from '@app/components/stations/advanced-filter/StationFilterUtils';
import {
  IndividualStationFilterOption,
  OptionType,
  StationFilterOption,
  StationGroupFilterOption,
  StationTypeFilterOption,
} from '@app/components/stations/advanced-filter/types';
import { useStationsGroups } from '@app/core/stations/hooks/useStationsGroupsQuery';
import { useStationsWithAudit } from '@app/modules/audit/hooks/useStationsWithAudit';
import { InfoM } from '@app/modules/audit/modules/aod/screens/aod-rule-view/components/InfoM';
import { auditReports } from '@mi-front/api-clients';
import { Box, Chip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { StationType } from '@app/core/types/stations';

interface GipReportStationsInfoProps {
  config: auditReports.GipReportDataDto;
}

export function GipReportStationsInfo({ config }: GipReportStationsInfoProps) {
  const { $t } = useIntl();
  const stations = useStationsWithAudit();
  const groups = useStationsGroups();

  // Check if we have advanced filtering
  if (config.stationsFilters?.length) {
    // Convert API filters to StationFilterOption format for display
    const stationFilters: StationFilterOption[] = config.stationsFilters
      .map((filter) => {
        switch (filter.type) {
          case auditReports.StationsFiltersTypeEnum.STATION_TYPE:
            return {
              type: OptionType.StationType,
              value: filter.value as StationType,
              label: getStationTypeLabel($t, filter.value as StationType),
            } as StationTypeFilterOption;

          case auditReports.StationsFiltersTypeEnum2.STATION_GROUP: {
            const group = groups.find((g) => g.id === filter.value);
            if (!group) return null;
            return {
              type: OptionType.StationGroup,
              value: group,
              label: group.name,
            } as StationGroupFilterOption;
          }
          case auditReports.StationsFiltersTypeEnum3.STATION_INDIVIDUAL: {
            const station = stations.find((s) => s.id === filter.value);
            if (!station) return null;
            return {
              type: OptionType.IndividualStation,
              value: station,
              label: station.name,
            } as IndividualStationFilterOption;
          }

          default:
            return null;
        }
      })
      .filter((filter): filter is StationFilterOption => filter !== null);

    return (
      <InfoM
        label={<FormattedMessage id="audit.gipReport.form.stations" defaultMessage="Emisoras" />}
      >
        {stationFilters.length ? (
          <StationFilterChipsList values={stationFilters} readonly />
        ) : (
          <FormattedMessage
            id="audit.gipReport.form.allStationsDefault"
            defaultMessage="Todas las emisoras"
          />
        )}
      </InfoM>
    );
  }

  // Fallback to simple station selection
  return (
    <InfoM
      label={<FormattedMessage id="audit.gipReport.form.stations" defaultMessage="Emisoras" />}
    >
      {!config.stationsIds?.length || config.stationsIds.length === stations.length ? (
        <FormattedMessage
          id="audit.gipReport.form.allStationsDefault"
          defaultMessage="Todas las emisoras"
        />
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {config.stationsIds
            ?.map((id) => stations.find((s) => s.id === id)!)
            .filter((s) => !!s)
            .map((s) => (
              <Chip key={s.id} label={s.name} variant="outlined" size="small" />
            ))}
        </Box>
      )}
    </InfoM>
  );
}
